/**
 * Importar a base de abrangência de mercado — a frota por marca, modelo, ano e
 * motor que o estudo de mercado usa.
 *
 * **Substitui a tela de colar SQL.** No sistema antigo, carregar esta base era
 * colar um `INSERT` e mandar executar; aqui o dado entra tipado e conferido.
 *
 * **A API aceita 5.000 registros por chamada, e a tabela tem 14.534.** Uma carga
 * completa é, portanto, mais de uma chamada — e isso muda o que "truncar" quer
 * dizer: truncar vai junto do **primeiro** lote, e entre esse lote e o último a
 * tabela fica com menos da metade do que tinha. Se algo falhar no meio, ela fica
 * assim. A tela diz isso antes, e é a razão de a confirmação ser digitada.
 *
 * **Lê CSV e texto colado, não .xlsx.** O front não carrega biblioteca de
 * planilha — as telas de Excel em lote mandam o arquivo para o servidor, que tem
 * ClosedXML, mas esta rota não recebe arquivo, e sim a lista tipada. Quem tem o
 * dado no Excel salva como CSV ou copia e cola: o separador é detectado.
 */

import { useMemo, useRef, useState } from "react"
import { useMutation } from "@tanstack/react-query"
import { api, foraDoProjeto } from "@/api/cliente"
import { AvisoDeDadoGlobal } from "@/kit/AvisoDeDadoGlobal"
import { BarraDeEstado } from "@/kit/BarraDeEstado"
import { ConfirmacaoDigitada } from "@/kit/ConfirmacaoDigitada"
import { Faixa } from "@/kit/Faixa"
import { Aviso, Botao, estiloCampo, mensagem } from "../outros/pecas"

/** O teto da API por chamada. Acima disso, a carga vai em lotes. */
const POR_CHAMADA = 5_000

const COLUNAS = [
  { chave: "marcaModelo", rotulo: "Marca/modelo", exemplo: "VW/GOL 1.0" },
  { chave: "marca", rotulo: "Marca", exemplo: "Volkswagen", obrigatorio: true },
  { chave: "modelo", rotulo: "Modelo", exemplo: "Gol", obrigatorio: true },
  { chave: "ano", rotulo: "Ano", exemplo: "2007", obrigatorio: true, numero: true },
  { chave: "motor", rotulo: "Motor", exemplo: "1.0", obrigatorio: true },
  { chave: "potencia", rotulo: "Potência", exemplo: "76" },
  { chave: "qtdVeiculoAno", rotulo: "Frota no ano", exemplo: "203694", numero: true },
  { chave: "qtdVeiculoAcumulado", rotulo: "Frota acumulada", exemplo: "1508822", numero: true },
] as const

type Registro = {
  marcaModelo: string
  marca: string
  modelo: string
  ano: number
  motor: string
  potencia: string
  qtdVeiculoAno: number
  qtdVeiculoAcumulado: number
}

export function ImportarAbrangencia() {
  const [texto, setTexto] = useState("")
  const [truncar, setTruncar] = useState(false)
  const [confirmando, setConfirmando] = useState(false)
  const [recado, setRecado] = useState<string | null>(null)
  const [andamento, setAndamento] = useState<string | null>(null)
  const entrada = useRef<HTMLInputElement>(null)

  const lido = useMemo(() => ler(texto), [texto])

  const importar = useMutation({
    mutationFn: async () => {
      const lotes = emLotes(lido.registros, POR_CHAMADA)
      let enviados = 0

      for (const [i, lote] of lotes.entries()) {
        setAndamento(`Enviando o lote ${i + 1} de ${lotes.length}…`)
        await api.post("api/estudo-mercado/importar", {
          // Truncar vai só no primeiro: no segundo apagaria o que o primeiro
          // acabou de gravar.
          truncarAntes: truncar && i === 0,
          registros: lote,
        }, foraDoProjeto())
        enviados += lote.length
      }

      return { enviados, lotes: lotes.length }
    },
    onSuccess: (r) => {
      setRecado(`${r.enviados.toLocaleString("pt-BR")} registro(s) importados em ${r.lotes} chamada(s).`)
      setConfirmando(false)
      setAndamento(null)
    },
    onError: (e) => { setRecado(mensagem(e)); setAndamento(null) },
  })

  function escolher(arquivo: File | undefined) {
    if (!arquivo) return
    setRecado(null)
    arquivo.text().then(setTexto).catch(() => setRecado("Não consegui ler o arquivo."))
  }

  const lotes = Math.ceil(lido.registros.length / POR_CHAMADA)

  return (
    <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <Faixa
        titulo="Importar abrangência de mercado"
        detalhe={lido.registros.length > 0
          ? `${lido.registros.length.toLocaleString("pt-BR")} linha(s) lidas`
          : "A frota por marca, modelo, ano e motor"}
      />

      <AvisoDeDadoGlobal oQueMuda="A abrangência é a base do estudo de mercado, e vale para o sistema inteiro" />

      <div style={{ flex: 1, minHeight: 0, overflow: "auto", padding: 16, maxWidth: "96ch" }}>
        <p style={{ margin: "0 0 12px", fontSize: 12.5, color: "var(--txt2)", lineHeight: 1.6 }}>
          Cole o conteúdo da planilha ou escolha um <b>CSV</b>. A primeira linha pode ser
          cabeçalho — se os nomes baterem com os de baixo, ela é usada; se não, a ordem das
          colunas é a da tabela. Separador detectado: tabulação, ponto-e-vírgula ou vírgula.
        </p>

        <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap", marginBottom: 10 }}>
          <input ref={entrada} type="file" accept=".csv,.txt" style={{ display: "none" }}
                 onChange={(e) => escolher(e.target.files?.[0])} />
          <Botao aoClicar={() => entrada.current?.click()}>Escolher CSV…</Botao>
          {texto && <Botao aoClicar={() => { setTexto(""); setRecado(null) }}>Limpar</Botao>}
        </div>

        <textarea
          value={texto}
          onChange={(e) => { setTexto(e.target.value); setRecado(null) }}
          rows={8}
          placeholder={COLUNAS.map((c) => c.exemplo).join("\t")}
          style={{
            ...estiloCampo, width: "100%", fontFamily: "var(--fm)", fontSize: 11.5,
            resize: "vertical",
          }}
        />

        <p style={{ margin: "6px 0 14px", fontSize: 10.5, color: "var(--txt3)", lineHeight: 1.5 }}>
          Colunas, nesta ordem: {COLUNAS.map((c) => c.rotulo).join(" · ")}.
        </p>

        {recado && <Aviso>{recado}</Aviso>}
        {andamento && (
          <p style={{ margin: "0 0 10px", fontSize: 12, color: "var(--txt2)" }}>{andamento}</p>
        )}

        {texto.trim() !== "" && <Conferencia lido={lido} />}

        {lido.registros.length > 0 && (
          <>
            <label style={{
              display: "flex", alignItems: "flex-start", gap: 7, fontSize: 12,
              margin: "14px 0 10px", lineHeight: 1.55, maxWidth: "72ch",
            }}>
              <input type="checkbox" checked={truncar}
                     onChange={(e) => { setTruncar(e.target.checked); setConfirmando(false) }}
                     style={{ marginTop: 3 }} />
              <span>
                <b>Apagar a base inteira antes</b> de carregar.
                {lotes > 1 && (
                  <> Atenção: são <b>{lotes} chamadas</b>, e o apagar vai só na primeira — entre
                  a primeira e a última a base fica parcial, e fica assim se alguma falhar.</>
                )}
              </span>
            </label>

            {!confirmando ? (
              <Botao
                destaque={!truncar}
                aoClicar={() => { setRecado(null); truncar ? setConfirmando(true) : importar.mutate() }}
                ocupado={importar.isPending}
                desligado={lido.problemas.length > 0}
              >
                {importar.isPending
                  ? "Importando…"
                  : truncar
                    ? "Apagar tudo e importar…"
                    : `Importar ${lido.registros.length.toLocaleString("pt-BR")} registro(s)`}
              </Botao>
            ) : (
              <ConfirmacaoDigitada
                alvo="apagar a base"
                rotuloDoBotao="Apagar e importar"
                ocupado={importar.isPending}
                oQueAcontece={
                  <>
                    Apaga <b>toda</b> a base de abrangência e carrega{" "}
                    <b>{lido.registros.length.toLocaleString("pt-BR")}</b> registro(s) no lugar,
                    em {lotes} chamada(s). O que está lá hoje <b>não volta</b> — só se você tiver
                    o arquivo de origem.
                  </>
                }
                aoConfirmar={() => importar.mutate()}
                aoCancelar={() => setConfirmando(false)}
              />
            )}
          </>
        )}
      </div>

      <BarraDeEstado
        direita={lido.registros.length > 0
          ? `${lotes} chamada(s) de até ${POR_CHAMADA.toLocaleString("pt-BR")}`
          : undefined}
      >
        {lido.problemas.length > 0
          ? `${lido.problemas.length} linha(s) com problema — corrija antes de importar.`
          : "A base tem 14.534 registros hoje; a API aceita 5.000 por chamada."}
      </BarraDeEstado>
    </div>
  )
}

// ------------------------------------------------------------------ conferência

function Conferencia({ lido }: { lido: Lido }) {
  const anos = [...new Set(lido.registros.map((r) => r.ano))].sort((a, b) => a - b)
  const marcas = [...new Set(lido.registros.map((r) => r.marca))].sort()

  return (
    <div style={{
      padding: 11, borderRadius: 7, border: "1px solid var(--line2)",
      background: "var(--surface2)", marginBottom: 4,
    }}>
      <p style={{ margin: "0 0 7px", fontSize: 12, lineHeight: 1.55 }}>
        <b>{lido.registros.length.toLocaleString("pt-BR")}</b> linha(s) válidas
        {lido.problemas.length > 0 && (
          <> · <b style={{ color: "var(--erro)" }}>{lido.problemas.length} com problema</b></>
        )}
        {anos.length > 0 && (
          <> · {anos.length} ano(s), de {anos[0]} a {anos[anos.length - 1]}</>
        )}
        {marcas.length > 0 && <> · {marcas.length} marca(s)</>}
        {lido.cabecalhoUsado && <> · a primeira linha foi lida como cabeçalho</>}
      </p>

      {lido.problemas.length > 0 && (
        <div style={{ marginTop: 6 }}>
          {lido.problemas.slice(0, 8).map((p) => (
            <p key={p.linha} style={{ margin: 0, fontSize: 10.5, color: "var(--erro)", lineHeight: 1.5 }}>
              linha {p.linha}: {p.porque}
            </p>
          ))}
          {lido.problemas.length > 8 && (
            <p style={{ margin: "3px 0 0", fontSize: 10.5, color: "var(--txt3)" }}>
              … e mais {lido.problemas.length - 8}.
            </p>
          )}
        </div>
      )}

      {lido.registros.length > 0 && (
        <div style={{ overflowX: "auto", marginTop: 8 }}>
          <table style={{ borderCollapse: "collapse", fontSize: 10.5 }}>
            <thead>
              <tr>
                {COLUNAS.map((c) => (
                  <th key={c.chave} style={{
                    textAlign: "left", padding: "3px 7px", color: "var(--txt3)",
                    borderBottom: "1px solid var(--line)", whiteSpace: "nowrap",
                  }}>
                    {c.rotulo}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {lido.registros.slice(0, 4).map((r, i) => (
                <tr key={i}>
                  {COLUNAS.map((c) => (
                    <td key={c.chave} style={{ padding: "2px 7px", whiteSpace: "nowrap" }}>
                      {String(r[c.chave as keyof Registro] ?? "")}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {lido.registros.length > 4 && (
            <p style={{ margin: "3px 0 0", fontSize: 10, color: "var(--txt3)" }}>
              … e mais {(lido.registros.length - 4).toLocaleString("pt-BR")} linhas.
            </p>
          )}
        </div>
      )}
    </div>
  )
}

// ------------------------------------------------------------------ leitura

export type Problema = { linha: number; porque: string }
export type Lido = { registros: Registro[]; problemas: Problema[]; cabecalhoUsado: boolean }

/**
 * Lê o texto colado ou o CSV.
 *
 * O separador é detectado pela primeira linha: quem cola do Excel traz tabulação,
 * quem salva CSV no Brasil traz ponto-e-vírgula, e quem exporta de ferramenta
 * inglesa traz vírgula. Adivinhar errado transformaria a planilha inteira numa
 * coluna só, e o erro apareceria como "marca obrigatória" em todas as linhas.
 */
export function ler(texto: string): Lido {
  const linhas = texto.split(/\r?\n/).filter((l) => l.trim() !== "")
  if (linhas.length === 0) return { registros: [], problemas: [], cabecalhoUsado: false }

  const sep = separadorDe(linhas[0]!)

  // Cabecalho: se a primeira linha nao tem numero no lugar do ano, e titulo.
  const primeira = linhas[0]!.split(sep)
  const cabecalhoUsado = !/^\d{4}$/.test((primeira[3] ?? "").trim())
  const corpo = cabecalhoUsado ? linhas.slice(1) : linhas

  const registros: Registro[] = []
  const problemas: Problema[] = []

  for (const [i, linha] of corpo.entries()) {
    const n = i + 1 + (cabecalhoUsado ? 1 : 0)
    const campos = linha.split(sep).map((c) => c.trim().replace(/^"|"$/g, ""))

    const r: Registro = {
      marcaModelo: campos[0] ?? "",
      marca: campos[1] ?? "",
      modelo: campos[2] ?? "",
      ano: numero(campos[3]),
      motor: campos[4] ?? "",
      potencia: campos[5] ?? "",
      qtdVeiculoAno: numero(campos[6]),
      qtdVeiculoAcumulado: numero(campos[7]),
    }

    // As mesmas regras do validador da API, para o erro aparecer aqui e não
    // depois de mandar 5.000 linhas.
    const faltando: string[] = []
    if (!r.marca) faltando.push("marca")
    if (!r.modelo) faltando.push("modelo")
    if (!r.motor) faltando.push("motor")

    if (faltando.length > 0) {
      problemas.push({ linha: n, porque: `sem ${faltando.join(", ")}` })
      continue
    }
    if (r.ano < 1950 || r.ano > 2100) {
      problemas.push({ linha: n, porque: `ano "${campos[3] ?? ""}" fora de 1950–2100` })
      continue
    }
    if (r.qtdVeiculoAno < 0 || r.qtdVeiculoAcumulado < 0) {
      problemas.push({ linha: n, porque: "frota negativa" })
      continue
    }

    registros.push(r)
  }

  return { registros, problemas, cabecalhoUsado }
}

function separadorDe(linha: string): string {
  if (linha.includes("\t")) return "\t"
  if (linha.includes(";")) return ";"
  return ","
}

/** Aceita "1.508.822" e "1508822"; o que não é número vira 0. */
function numero(v: string | undefined): number {
  const limpo = (v ?? "").replace(/[^\d-]/g, "")
  const n = Number(limpo)
  return Number.isFinite(n) ? n : 0
}

export function emLotes<T>(itens: T[], tamanho: number): T[][] {
  const lotes: T[][] = []
  for (let i = 0; i < itens.length; i += tamanho) lotes.push(itens.slice(i, i + tamanho))
  return lotes
}
